import { useState, useRef, useCallback, useEffect } from 'react'
import { Save, RotateCcw, Settings } from 'lucide-react'
import { ConfigEditor, ConfigEditorHandle } from '@/components/config/ConfigEditor'

/**
 * Direktorkonfig v2 (D11): Eigener, erweiterbarer Director-Einstellungen-
 * Bereich — dieselben Karten wie beim Superadmin, beginnt mit
 * «Abwesenheitsarten». Sichtbarkeit/Bearbeitbarkeit regelt der Superadmin
 * pro Karte/Feld und pro Direktor (D12/D13); die Permissions kommen
 * serverseitig mit der Config-Response (field_permissions) und schalten
 * im Editor Felder auf read-only/unsichtbar. Direktoren sehen keine
 * Delegations-Controls (hideDelegation).
 */
export function DirectorSettingsPage() {
  const editorRef = useRef<ConfigEditorHandle>(null)
  const successTimeoutRef = useRef<number | null>(null)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [configLabel, setConfigLabel] = useState('')
  const [configDescription, setConfigDescription] = useState('')

  const handleConfigLoaded = useCallback((label: string, description: string) => {
    setConfigLabel(label)
    setConfigDescription(description)
  }, [])

  const handleError = useCallback((error: string | null) => {
    setError(error)
  }, [])

  const handleSuccess = useCallback(() => {
    setSuccess(true)
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current)
    }
    successTimeoutRef.current = setTimeout(() => setSuccess(false), 5000)
  }, [])

  const handleDirtyChange = useCallback((isDirty: boolean) => {
    setDirty(isDirty)
  }, [])

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current)
      }
    }
  }, [])

  const handleSave = async () => {
    if (!editorRef.current?.isDirty()) return
    setSaving(true)
    await editorRef.current.save()
    setSaving(false)
  }

  const handleReset = () => {
    editorRef.current?.reset()
  }

  return (
    <div className="flex flex-col min-h-full">
      {/* Header */}
      <div className="px-4 lg:px-6 py-3 border-b bg-white flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
            <Settings className="w-5 h-5 text-indigo-600" />
          </div>
          <h1 className="text-lg font-bold">{configLabel || 'Einstellungen'}</h1>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <button onClick={handleReset} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 px-2 py-1">
              <RotateCcw className="w-3.5 h-3.5" /> Verwerfen
            </button>
          )}
          <button onClick={handleSave} disabled={saving || !dirty}
            className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-1.5 disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>

      {configDescription && (
        <div className="px-4 lg:px-6 py-3 bg-white border-b">
          <p className="text-sm text-gray-600">{configDescription}</p>
        </div>
      )}

      {error && <div className="bg-red-50 px-4 lg:px-6 py-3 text-sm text-red-700">{error}</div>}
      {success && <div className="bg-green-50 px-4 lg:px-6 py-3 text-sm text-green-700">Einstellungen gespeichert</div>}

      <ConfigEditor
        ref={editorRef}
        configType="director"
        hideDelegation
        onDirtyChange={handleDirtyChange}
        onError={handleError}
        onSuccess={handleSuccess}
        onConfigLoaded={handleConfigLoaded}
      />
    </div>
  )
}
